// src/components/Contact/ContactCentered.js
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Snackbar,
  Alert,
  Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EmailIcon from "@mui/icons-material/Email";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SendIcon from "@mui/icons-material/Send";
import emailjs from "@emailjs/browser"; // <-- EmailJS

// ENV (CRA)
const SERVICE_ID  = process.env.REACT_APP_EMAILJS_SERVICE_ID;   // service_wu1erbt
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;  // template_042174b
const PUBLIC_KEY  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;   // nIuuYQskFc5tKGm8t

const COLORS = {
  bg: "#FAF7F2",
  primaryDark: "#2C2416",
  primaryMid: "#5C4033",
  accent: "#D2691E",
  accentHover: "#B8590E",
  white: "#FFFFFF",
  shadow: "0 8px 30px rgba(0,0,0,0.08)",
};

const Page = styled(Box)({
  minHeight: "100vh",
  backgroundColor: COLORS.bg,
  display: "flex",
  flexDirection: "column",
});

const Hero = styled(Box)(({ theme }) => ({
  width: "100%",
  background: "linear-gradient(135deg, #2C2416 0%, #5C4033 100%)",
  color: COLORS.white,
  textAlign: "center",
  padding: theme.spacing(6, 2),
}));

const Shell = styled(Container)(({ theme }) => ({
  maxWidth: "400px",
  marginTop: theme.spacing(6),
  marginBottom: theme.spacing(8),
}));

const ClickableCard = styled(Card)(({ theme }) => ({
  height: "100%",
  borderRadius: theme.spacing(2),
  boxShadow: COLORS.shadow,
  backgroundColor: COLORS.white,
  padding: theme.spacing(3),
  textAlign: "center",
  transition: "transform .25s ease, box-shadow .25s ease",
  cursor: "pointer",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
  },
}));

const IconWrap = styled(Box)({
  width: 48,
  height: 48,
  display: "grid",
  placeItems: "center",
  borderRadius: 12,
  backgroundColor: "#FCE9DD",
  margin: "0 auto",
});

const FormCard = styled(Card)(({ theme }) => ({
  width: "100%",
  maxWidth: 640,
  backgroundColor: COLORS.white,
  boxShadow: COLORS.shadow,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  textAlign: "center",
  margin: "0 auto",
}));

const AccentButton = styled(Button)(({ theme }) => ({
  backgroundColor: COLORS.accent,
  color: COLORS.white,
  textTransform: "none",
  fontWeight: 600,
  padding: theme.spacing(1.5, 4),
  borderRadius: 8,
  "&:hover": {
    backgroundColor: COLORS.accentHover,
  },
}));

// Facebook Brand Logo SVG - Official Facebook Blue
const FacebookLogo = ({ sx = {} }) => (
  <Box sx={{ display: "inline-flex", ...sx }}>
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block" }}
    >
      <path
        d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
        fill="#1877F2"
      />
    </svg>
  </Box>
);

// Instagram Brand Logo SVG - Official Instagram Gradient
const InstagramLogo = ({ sx = {} }) => {
  const gradientId = `instagram-gradient-${Math.random().toString(36).substr(2, 9)}`;
  return (
    <Box sx={{ display: "inline-flex", ...sx }}>
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: "block" }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#E4405F" />
            <stop offset="50%" stopColor="#FCAF45" />
            <stop offset="100%" stopColor="#833AB4" />
          </linearGradient>
        </defs>
        <path
          d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"
          fill={`url(#${gradientId})`}
        />
      </svg>
    </Box>
  );
};

export default function ContactCentered() {
  const location = useLocation();
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", location: "", message: "" });
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  // Scroll to top when component mounts or location changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const allFilled = Object.values(form).every((v) => v.trim() !== "");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");

    if (!allFilled) return;

    try {
      const templateParams = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        location: form.location,
        message: form.message,
        time: new Date().toLocaleString(), // optional; template can use {{time}}
      };

      const res = await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, {
        publicKey: PUBLIC_KEY,
      });

      if (res?.status === 200) {
        setSent(true);
        setForm({ firstName: "", lastName: "", email: "", phone: "", location: "", message: "" });
      } else {
        setErr("Failed to send. Please try again.");
      }
    } catch (e2) {
      console.error("EmailJS error:", e2);
      setErr(
        "Send failed. Check EmailJS origins (localhost:3000), keys, Service/Template IDs, or Gmail connection."
      );
    }
  };

  // Clickable cards
  const handleEmailClick = () => {
    window.location.href = "mailto:sdsolutionstexas@gmail.com";
  };
  const handlePhoneClick = () => {
    window.location.href = "tel:+15082413648";
  };
  const handleLocationClick = () => {
    window.open(
      "https://www.google.com/maps/place/SD+Solutions+LLC/@33.2765971,-96.5874098,17z/data=!4m16!1m9!3m8!1s0x22bbdd13f4360f2d:0x4ef1d7de893f6441!2sSD+Solutions+LLC!8m2!3d33.2762893!4d-96.5849231!9m1!1b1!16s%2Fg%2F11yrjqccpn!3m5!1s0x22bbdd13f4360f2d:0x4ef1d7de893f6441!8m2!3d33.2762893!4d-96.5849231!16s%2Fg%2F11yrjqccpn?entry=ttu&g_ep=EgoyMDI1MTIwOS4wIKXMDSoASAFQAw%3D%3D",
      "_blank",
      "noopener,noreferrer"
    );
  };
  const handleFacebookClick = () => {
    window.open(
      "https://www.facebook.com/share/1C2CypNkYM/?mibextid=wwXIfr",
      "_blank",
      "noopener,noreferrer"
    );
  };
  const handleInstagramClick = () => {
    window.open(
      "https://www.instagram.com/sdsoln?igsh=MTRkM3FsZDBrbDl3OA==",
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <Page>
      <Hero>
        <Container maxWidth="md">
          <Typography
            sx={{ color: "#FFFFFF" }}
            variant="h2"
            component="h1"
            gutterBottom
            fontWeight="bold"
          >
            Get In Touch
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.95, color: "#FFFFFF" }}>
            Reach us via email, phone, visit or drop us a note.
          </Typography>
        </Container>
      </Hero>

      <Shell>
        {/* INFO CARDS */}
        <Grid container spacing={2} justifyContent="center" sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <ClickableCard onClick={handleEmailClick}>
              <IconWrap>
                <EmailIcon sx={{ color: COLORS.accent }} />
              </IconWrap>
              <Typography variant="subtitle1" sx={{ mt: 1.5, fontWeight: 800, color: COLORS.primaryDark }}>
                Email Us
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.primaryMid, mt: 0.5, "&:hover": { color: COLORS.accent } }}>
                sdsolutionstexas@gmail.com
              </Typography>
            </ClickableCard>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <ClickableCard onClick={handlePhoneClick}>
              <IconWrap>
                <LocalPhoneIcon sx={{ color: COLORS.accent }} />
              </IconWrap>
              <Typography variant="subtitle1" sx={{ mt: 1.5, fontWeight: 800, color: COLORS.primaryDark }}>
                Call Us
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.primaryMid, mt: 0.5, "&:hover": { color: COLORS.accent } }}>
                +1 (508) 241-3648
              </Typography>
            </ClickableCard>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <ClickableCard onClick={handleLocationClick}>
              <IconWrap>
                <LocationOnIcon sx={{ color: COLORS.accent }} />
              </IconWrap>
              <Typography variant="subtitle1" sx={{ mt: 1.5, fontWeight: 800, color: COLORS.primaryDark }}>
                Location
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.primaryMid, mt: 0.5, "&:hover": { color: COLORS.accent } }}>
                1902 Gardenia Ct, Melissa, TX 75454
              </Typography>
            </ClickableCard>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <ClickableCard onClick={handleFacebookClick}>
              <IconWrap>
                <FacebookLogo />
              </IconWrap>
              <Typography variant="subtitle1" sx={{ mt: 1.5, fontWeight: 800, color: COLORS.primaryDark }}>
                Facebook
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.primaryMid, mt: 0.5, "&:hover": { color: COLORS.accent } }}>
                Follow us on Facebook
              </Typography>
            </ClickableCard>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <ClickableCard onClick={handleInstagramClick}>
              <IconWrap>
                <InstagramLogo />
              </IconWrap>
              <Typography variant="subtitle1" sx={{ mt: 1.5, fontWeight: 800, color: COLORS.primaryDark }}>
                Instagram
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.primaryMid, mt: 0.5, "&:hover": { color: COLORS.accent } }}>
                Follow us on Instagram
              </Typography>
            </ClickableCard>
          </Grid>
        </Grid>

        {/* CONTACT FORM */}
        <FormCard>
          <CardContent>
            <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.primaryDark, mb: 2 }}>
              Send us a Message
            </Typography>

            <Box component="form" onSubmit={onSubmit} noValidate>
              {/* First Name and Last Name - side by side on desktop, stacked on mobile */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="First Name"
                    name="firstName"
                    value={form.firstName}
                    onChange={onChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Last Name"
                    name="lastName"
                    value={form.lastName}
                    onChange={onChange}
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                required
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                required
                label="Phone"
                name="phone"
                value={form.phone}
                onChange={onChange}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                required
                label="Location"
                name="location"
                value={form.location}
                onChange={onChange}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                required
                label="Message"
                name="message"
                multiline
                minRows={4}
                value={form.message}
                onChange={onChange}
                sx={{ mb: 2 }}
              />

              <AccentButton type="submit" endIcon={<SendIcon />} disabled={!allFilled}>
                Send Message
              </AccentButton>

              {!!err && (
                <Typography variant="body2" sx={{ color: "#b91c1c", mt: 1 }}>
                  {err}
                </Typography>
              )}
            </Box>
          </CardContent>
        </FormCard>
      </Shell>

      <Snackbar
        open={sent}
        autoHideDuration={3000}
        onClose={() => setSent(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSent(false)} severity="success" variant="filled" sx={{ backgroundColor: COLORS.accent }}>
          Thank you! Your message has been sent.
        </Alert>
      </Snackbar>
    </Page>
  );
}
